import {
  ArrowLeft,
  Check,
  ChevronLeft,
  ChevronRight,
  Download,
  Layers,
  Loader2,
  Maximize2,
  Minimize2,
  Pencil,
  Play,
  Presentation,
  RefreshCw,
  Send,
  Sparkles,
  Trash2,
  X,
} from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getApiBaseUrl } from "@/config/runtime";
import { useOperations } from "@/context/OperationContext";
import { type OperationEvent, useOperationStream } from "@/hooks/useOperationStream";
import { getHeaders } from "@/lib/api";
import { presentationsService } from "@/lib/services";
import type { PresentationProjectResponse, PresentationProjectStatus } from "@/types";

// ---------------------------------------------------------------------------
// Authenticated Image Component — fetches images from auth-protected
// API endpoints using Bearer tokens and renders via object URLs.
// ---------------------------------------------------------------------------

function AuthImage({ src, alt, className, ...rest }: React.ImgHTMLAttributes<HTMLImageElement>) {
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!src) return;

    // External/absolute URLs (SAS URLs) — no auth needed
    if (src.startsWith("http://") || src.startsWith("https://")) {
      setObjectUrl(src);
      return;
    }

    // Internal API paths — fetch with auth headers
    let cancelled = false;

    (async () => {
      try {
        const baseUrl = getApiBaseUrl();
        const fullUrl = `${baseUrl}${src}`;
        const headers = await getHeaders();
        headers.delete("Content-Type");
        const response = await fetch(fullUrl, { headers, cache: "no-store" });
        if (!response.ok || cancelled) return;
        const blob = await response.blob();
        if (cancelled) return;
        setObjectUrl(URL.createObjectURL(blob));
      } catch {
        if (!cancelled) setError(true);
      }
    })();

    return () => {
      cancelled = true;
      setObjectUrl((prev) => {
        if (prev && !prev.startsWith("http")) URL.revokeObjectURL(prev);
        return null;
      });
    };
  }, [src]);

  if (error || !objectUrl) {
    return (
      <div className={`flex items-center justify-center bg-surface-secondary ${className ?? ""}`}>
        <Presentation className="w-8 h-8 text-content-tertiary" />
      </div>
    );
  }

  return <img src={objectUrl} alt={alt} className={className} {...rest} />;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const STATUS_LABELS: Record<PresentationProjectStatus, string> = {
  draft: "Draft",
  outlining: "Generating Outline...",
  outline_ready: "Outline Ready",
  generating: "Generating Slides...",
  generated: "Generated",
  refining: "Refining...",
  complete: "Complete",
};

const STATUS_COLORS: Record<PresentationProjectStatus, string> = {
  draft: "bg-slate-500/20 text-slate-400",
  outlining: "bg-amber-500/20 text-amber-400",
  outline_ready: "bg-blue-500/20 text-blue-400",
  generating: "bg-amber-500/20 text-amber-400",
  generated: "bg-emerald-500/20 text-emerald-400",
  refining: "bg-amber-500/20 text-amber-400",
  complete: "bg-emerald-500/20 text-emerald-400",
};

type TabId = "outline-solid" | "preview" | "refine";

type ChatMessage = { role: "user" | "assistant"; content: string };

/**
 * Reconstruct refine conversation history from project versions.
 * Each version after v1 stores `refinementInstructions` and
 * `refinedSlideNumbers` in its generationMetadata. We replay those
 * into a chat-style history array.
 */
function buildHistoryFromVersions(versions: PresentationProjectResponse["versions"]): ChatMessage[] {
  const history: ChatMessage[] = [];
  // Versions are returned newest-first from the API; sort by version number
  const sorted = [...versions].sort((a, b) => a.versionNumber - b.versionNumber);
  for (const v of sorted) {
    const meta = v.generationMetadata;
    const instructions = meta?.refinement_instructions as string | undefined;
    if (!instructions) continue; // v1 (initial generation) — no refinement

    const slideNums = meta?.refined_slide_numbers as number[] | undefined;
    const genTime = meta?.generation_time_seconds as number | undefined;

    const slideSuffix = slideNums && slideNums.length > 0 ? ` (slides: ${slideNums.join(", ")})` : "";
    const timeSuffix = genTime ? ` (${genTime}s)` : "";

    history.push(
      {
        role: "user",
        content: `${instructions}${slideSuffix}`,
      },
      {
        role: "assistant",
        content: `Refinement complete! Created version ${v.versionNumber} with ${v.slideCount} slides${timeSuffix}.`,
      },
    );
  }
  return history;
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

export function PresentationsProjectDetail() {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();

  const [project, setProject] = useState<PresentationProjectResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<TabId>("outline-solid");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [refineHistory, setRefineHistory] = useState<ChatMessage[]>([]);

  useEffect(() => {
    if (projectId) loadProject();
  }, [projectId]);

  const loadProject = async () => {
    if (!projectId) return;
    try {
      setLoading(true);
      const response = await presentationsService.getProject(projectId);
      setProject(response.data);

      // Reconstruct refine history from version metadata
      if (response.data.versions?.length > 0) {
        setRefineHistory(buildHistoryFromVersions(response.data.versions));
      }

      // Auto-select tab based on status
      const status = response.data.status;
      if (status === "generated" || status === "complete" || status === "refining") {
        setActiveTab("preview");
      } else if (status === "outline_ready") {
        setActiveTab("outline-solid");
      }
    } catch (err) {
      console.error("Failed to load project:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!projectId) return;
    setDeleting(true);
    try {
      await presentationsService.deleteProject(projectId);
      navigate("/apps/presentations");
    } catch (err) {
      console.error("Failed to delete project:", err);
    } finally {
      setDeleting(false);
    }
  };

  if (loading || !project) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="w-6 h-6 animate-spin text-content-tertiary" />
      </div>
    );
  }

  const previewSuffix = project.slides.length > 0 ? ` (${project.slides.length})` : "";
  const tabs: { id: TabId; label: string; disabled?: boolean }[] = [
    { id: "outline-solid", label: "Outline" },
    {
      id: "preview",
      label: `Preview${previewSuffix}`,
      disabled: !project.slides.length && project.status !== "generating",
    },
    {
      id: "refine",
      label: "Refine",
      disabled: project.status !== "generated" && project.status !== "complete",
    },
  ];

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="shrink-0 border-b border-border-primary px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate("/apps/presentations")}
              className="p-1.5 rounded-lg hover:bg-surface-secondary text-content-tertiary hover:text-content-primary transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-lg font-semibold text-content-primary">{project.name}</h1>
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[project.status]}`}>
                  {STATUS_LABELS[project.status]}
                </span>
              </div>
              {project.description && <p className="text-xs text-content-tertiary mt-0.5">{project.description}</p>}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {(project.status === "generated" || project.status === "complete") && (
              <button
                onClick={async () => {
                  try {
                    const blob = await presentationsService.exportPresentation(project.uuid);
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement("a");
                    a.href = url;
                    a.download = `${project.name.replace(/[^\w\s-]/g, "").trim()}.pptx`;
                    a.click();
                    URL.revokeObjectURL(url);
                  } catch (err) {
                    console.error("Export failed:", err);
                  }
                }}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-500/10 text-emerald-400 text-sm font-medium hover:bg-emerald-500/20 transition-colors"
              >
                <Download className="w-4 h-4" />
                Export PPTX
              </button>
            )}
            <button
              onClick={() => setShowDeleteModal(true)}
              className="p-1.5 rounded-lg hover:bg-red-500/10 text-content-tertiary hover:text-red-400 transition-colors"
              title="Delete project"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 mt-3">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => !tab.disabled && setActiveTab(tab.id)}
              disabled={tab.disabled}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${(() => {
                if (activeTab === tab.id) return "bg-surface-secondary text-content-primary";
                if (tab.disabled) return "text-content-tertiary/50 cursor-not-allowed";
                return "text-content-tertiary hover:text-content-primary hover:bg-surface-secondary/50";
              })()}`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <div className="flex-1 overflow-auto min-h-0">
        {activeTab === "outline-solid" && (
          <OutlineTab project={project} onProjectUpdate={(p) => setProject(p)} onSwitchTab={setActiveTab} />
        )}
        {activeTab === "preview" && <PreviewTab project={project} />}
        {activeTab === "refine" && (
          <RefineTab
            project={project}
            onProjectUpdate={(p) => setProject(p)}
            history={refineHistory}
            onHistoryChange={setRefineHistory}
          />
        )}
      </div>

      {/* Delete Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-surface-primary border border-border-primary rounded-xl p-6 max-w-sm w-full mx-4">
            <h3 className="text-lg font-semibold text-content-primary mb-2">Delete Presentation?</h3>
            <p className="text-sm text-content-tertiary mb-4">
              This will permanently delete "{project.name}" and all generated slides. This action cannot be undone.
            </p>
            <div className="flex items-center gap-2 justify-end">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="px-3 py-1.5 rounded-lg border border-border-primary text-content-secondary text-sm hover:bg-surface-secondary transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-500/10 text-red-400 text-sm font-medium hover:bg-red-500/20 transition-colors disabled:opacity-50"
              >
                {deleting && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Outline Tab
// ---------------------------------------------------------------------------

function OutlineTab({
  project,
  onProjectUpdate,
  onSwitchTab,
}: Readonly<{
  project: PresentationProjectResponse;
  onProjectUpdate: (p: PresentationProjectResponse) => void;
  onSwitchTab: (tab: TabId) => void;
}>) {
  const { trackOperation } = useOperations();
  const [generating, setGenerating] = useState(false);
  const [generatingSlides, setGeneratingSlides] = useState(false);
  const [streamContent, setStreamContent] = useState("");
  const [statusMessage, setStatusMessage] = useState("");
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editForm, setEditForm] = useState({ title: "", bullet_points: [""] });
  const [targetSlideCount, setTargetSlideCount] = useState(8);
  const [additionalInstructions, setAdditionalInstructions] = useState("");

  // Operation streams
  const [outlineOperationId, setOutlineOperationId] = useState<string | null>(null);
  const [slidesOperationId, setSlidesOperationId] = useState<string | null>(null);

  const handleOutlineEvent = useCallback(
    (event: OperationEvent) => {
      const { event: eventName, data } = event;

      switch (eventName) {
        case "content_delta": {
          const d = data as { delta?: string };
          setStreamContent((prev) => prev + (d.delta || ""));
          break;
        }
        case "outline_end": {
          // Reload project to get saved outline
          presentationsService.getProject(project.uuid).then((response) => {
            onProjectUpdate(response.data);
          });
          break;
        }
        case "operation_completed": {
          setGenerating(false);
          setStreamContent("");
          setOutlineOperationId(null);
          break;
        }
        case "operation_failed": {
          const d = data as { error?: string };
          console.error("Outline error:", d.error);
          setGenerating(false);
          setStreamContent("");
          setOutlineOperationId(null);
          break;
        }
        case "error": {
          const d = data as { message?: string };
          console.error("Outline error:", d.message);
          break;
        }
      }
    },
    [project.uuid, onProjectUpdate],
  );

  const handleSlidesEvent = useCallback(
    (event: OperationEvent) => {
      const { event: eventName, data } = event;

      switch (eventName) {
        case "status": {
          const d = data as { message?: string };
          setStatusMessage(d.message || "");
          break;
        }
        case "generation_end": {
          presentationsService.getProject(project.uuid).then((response) => {
            onProjectUpdate(response.data);
            onSwitchTab("preview");
          });
          break;
        }
        case "operation_completed": {
          setGeneratingSlides(false);
          setSlidesOperationId(null);
          break;
        }
        case "operation_failed": {
          const d = data as { error?: string };
          console.error("Generation error:", d.error);
          setStatusMessage(`Error: ${d.error}`);
          setGeneratingSlides(false);
          setSlidesOperationId(null);
          break;
        }
        case "error": {
          const d = data as { message?: string };
          console.error("Generation error:", d.message);
          setStatusMessage(`Error: ${d.message}`);
          break;
        }
      }
    },
    [project.uuid, onProjectUpdate, onSwitchTab],
  );

  useOperationStream(outlineOperationId, {
    onEvent: handleOutlineEvent,
    onError: (err) => {
      console.error("Outline stream error:", err);
      setGenerating(false);
      setStreamContent("");
      setOutlineOperationId(null);
    },
  });

  useOperationStream(slidesOperationId, {
    onEvent: handleSlidesEvent,
    onError: (err) => {
      console.error("Slides stream error:", err);
      setGeneratingSlides(false);
      setStatusMessage("Generation failed");
      setSlidesOperationId(null);
    },
  });

  const outline = project.outline || [];
  const hasOutline = outline.length > 0;

  const handleGenerateOutline = async () => {
    setGenerating(true);
    setStreamContent("");
    try {
      const result = await presentationsService.generateOutline(project.uuid, {
        targetSlideCount,
        additionalInstructions: additionalInstructions || undefined,
      });
      setOutlineOperationId(result.operation_id);
      trackOperation({
        operation_id: result.operation_id,
        operation_type: "presentation_outline",
        entity_id: project.uuid,
        status: "running",
        title: project.name || "Presentation outline-solid",
        started_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        user_id: 0,
      });
    } catch (err) {
      console.error("Failed to generate outline:", err);
      setGenerating(false);
      setStreamContent("");
    }
  };

  const handleGenerateSlides = async () => {
    setGeneratingSlides(true);
    setStatusMessage("Starting generation...");
    try {
      const result = await presentationsService.generateSlides(project.uuid, {});
      setSlidesOperationId(result.operation_id);
      trackOperation({
        operation_id: result.operation_id,
        operation_type: "presentation_slides",
        entity_id: project.uuid,
        status: "running",
        title: project.name || "Presentation slides",
        started_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        user_id: 0,
      });
    } catch (err) {
      console.error("Failed to generate slides:", err);
      setStatusMessage("Generation failed");
      setGeneratingSlides(false);
    }
  };

  const handleEditSlide = (index: number) => {
    const slide = outline[index];
    setEditingIndex(index);
    setEditForm({
      title: slide.title,
      bullet_points: [...(slide.bullet_points || [""]), ""],
    });
  };

  const handleSaveEdit = async () => {
    if (editingIndex === null) return;
    const updated = [...outline];
    updated[editingIndex] = {
      ...updated[editingIndex],
      title: editForm.title,
      bullet_points: editForm.bullet_points.filter((bp) => bp.trim()),
    };
    try {
      const response = await presentationsService.updateOutline(project.uuid, {
        slides: updated,
      });
      onProjectUpdate(response.data);
      setEditingIndex(null);
    } catch (err) {
      console.error("Failed to update outline:", err);
    }
  };

  const handleDeleteSlide = async (index: number) => {
    const updated = outline.filter((_, i) => i !== index);
    try {
      const response = await presentationsService.updateOutline(project.uuid, {
        slides: updated,
      });
      onProjectUpdate(response.data);
    } catch (err) {
      console.error("Failed to delete slide:", err);
    }
  };

  // Draft state — show generation form
  if (!hasOutline && !generating) {
    return (
      <div className="p-6 max-w-2xl">
        <div className="space-y-5">
          <div>
            <h2 className="text-base font-semibold text-content-primary mb-1">Generate Outline</h2>
            <p className="text-sm text-content-tertiary">
              AI will create a structured outline based on your project context. You can edit it before generating
              slides.
            </p>
          </div>

          <div>
            <label htmlFor="target-slide-count" className="block text-sm font-medium text-content-secondary mb-1.5">
              Target Slide Count
            </label>
            <div className="flex items-center gap-3">
              <input
                id="target-slide-count"
                type="range"
                min={3}
                max={15}
                value={targetSlideCount}
                onChange={(e) => setTargetSlideCount(Number(e.target.value))}
                className="flex-1 accent-mutual-orange"
              />
              <span className="text-sm font-medium text-content-primary w-8 text-center">{targetSlideCount}</span>
            </div>
          </div>

          <div>
            <label
              htmlFor="additional-instructions"
              className="block text-sm font-medium text-content-secondary mb-1.5"
            >
              Additional Instructions (optional)
            </label>
            <textarea
              id="additional-instructions"
              value={additionalInstructions}
              onChange={(e) => setAdditionalInstructions(e.target.value)}
              placeholder="e.g. Focus on ROI metrics, include a timeline slide, keep it high-level..."
              rows={3}
              className="w-full px-3 py-2 rounded-lg bg-surface-secondary border border-border-primary text-content-primary text-sm placeholder:text-content-tertiary focus:outline-hidden focus:ring-1 focus:ring-mutual-orange/50 resize-none"
            />
          </div>

          <button
            onClick={handleGenerateOutline}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-brand-gradient text-white text-sm font-medium hover:opacity-90 transition-opacity"
          >
            <Sparkles className="w-4 h-4" />
            Generate Outline
          </button>
        </div>
      </div>
    );
  }

  // Generating state
  if (generating) {
    return (
      <div className="p-6 max-w-2xl">
        <div className="flex items-center gap-2 mb-4">
          <Loader2 className="w-4 h-4 animate-spin text-mutual-orange" />
          <span className="text-sm font-medium text-content-primary">Generating outline...</span>
        </div>
        {streamContent && (
          <pre className="text-xs text-content-tertiary bg-surface-secondary rounded-lg p-4 overflow-auto max-h-96 whitespace-pre-wrap">
            {streamContent}
          </pre>
        )}
      </div>
    );
  }

  // Outline ready — show editable outline + generate button
  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-base font-semibold text-content-primary">Outline ({outline.length} slides)</h2>
          <p className="text-xs text-content-tertiary">Review and edit your outline, then generate slides.</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleGenerateOutline}
            disabled={generating}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border-primary text-content-secondary text-sm hover:bg-surface-secondary transition-colors"
          >
            <RefreshCw className="w-3.5 h-3.5" />
            Regenerate
          </button>
          <button
            onClick={handleGenerateSlides}
            disabled={generatingSlides}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-brand-gradient text-white text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
          >
            {generatingSlides ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                {statusMessage || "Generating..."}
              </>
            ) : (
              <>
                <Play className="w-4 h-4" />
                Generate Slides
              </>
            )}
          </button>
        </div>
      </div>

      {generatingSlides && statusMessage && (
        <div className="mb-4 px-4 py-3 rounded-lg bg-amber-500/10 border border-amber-500/20 text-amber-400 text-sm flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin shrink-0" />
          {statusMessage}
        </div>
      )}

      <div className="space-y-2 max-w-3xl">
        {outline.map((slide, index) => (
          <div
            key={`slide-${slide.title}-${index}`}
            className="rounded-xl border border-border-primary bg-surface-primary p-4 group"
          >
            {editingIndex === index ? (
              /* Edit mode */
              <div className="space-y-3">
                <input
                  type="text"
                  value={editForm.title}
                  onChange={(e) => setEditForm((f) => ({ ...f, title: e.target.value }))}
                  className="w-full px-3 py-1.5 rounded-lg bg-surface-secondary border border-border-primary text-content-primary text-sm focus:outline-hidden focus:ring-1 focus:ring-mutual-orange/50"
                  placeholder="Slide title"
                />
                {editForm.bullet_points.map((bp, bpIndex) => (
                  <div key={`edit-bp-${bpIndex}`} className="flex items-center gap-2">
                    {" "}
                    {/* NOSONAR — index key safe for editable bullet point list; items are positionally bound to form inputs */}
                    <span className="text-xs text-content-tertiary w-4">{bpIndex + 1}.</span>
                    <input
                      type="text"
                      value={bp}
                      onChange={(e) => {
                        const updated = [...editForm.bullet_points];
                        updated[bpIndex] = e.target.value;
                        // Auto-add new empty row
                        if (bpIndex === editForm.bullet_points.length - 1 && e.target.value) {
                          updated.push("");
                        }
                        setEditForm((f) => ({ ...f, bullet_points: updated })); // NOSONAR — state updater spreading form fields; complexity inherent to immutable update pattern
                      }}
                      className="flex-1 px-2 py-1 rounded-sm bg-surface-secondary border border-border-primary text-content-primary text-sm focus:outline-hidden focus:ring-1 focus:ring-mutual-orange/50"
                      placeholder="Bullet point..."
                    />
                  </div>
                ))}
                <div className="flex items-center gap-2">
                  <button
                    onClick={handleSaveEdit}
                    className="flex items-center gap-1 px-2 py-1 rounded-lg bg-emerald-500/10 text-emerald-400 text-xs font-medium hover:bg-emerald-500/20"
                  >
                    <Check className="w-3 h-3" /> Save
                  </button>
                  <button
                    onClick={() => setEditingIndex(null)}
                    className="flex items-center gap-1 px-2 py-1 rounded-lg bg-slate-500/10 text-content-tertiary text-xs hover:bg-slate-500/20"
                  >
                    <X className="w-3 h-3" /> Cancel
                  </button>
                </div>
              </div>
            ) : (
              /* View mode */
              <div className="flex items-start gap-3">
                <div className="shrink-0 w-7 h-7 rounded-lg bg-mutual-orange/10 flex items-center justify-center text-xs font-semibold text-mutual-orange">
                  {index + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="text-sm font-medium text-content-primary">{slide.title}</h4>
                  {slide.bullet_points && slide.bullet_points.length > 0 && (
                    <ul className="mt-1.5 space-y-0.5">
                      {slide.bullet_points.map((bp, bpIdx) => (
                        <li
                          key={`bp-${bp.slice(0, 20)}-${bpIdx}`}
                          className="text-xs text-content-tertiary flex items-start gap-1.5"
                        >
                          <span className="text-content-tertiary/50 mt-0.5">-</span>
                          {bp}
                        </li>
                      ))}
                    </ul>
                  )}
                  {slide.layout && (
                    <span className="inline-block mt-1.5 px-1.5 py-0.5 rounded-sm text-[10px] bg-surface-secondary text-content-tertiary">
                      {slide.layout}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    onClick={() => handleEditSlide(index)}
                    className="p-1 rounded-sm hover:bg-surface-secondary text-content-tertiary hover:text-content-primary"
                    title="Edit"
                  >
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={() => handleDeleteSlide(index)}
                    className="p-1 rounded-sm hover:bg-red-500/10 text-content-tertiary hover:text-red-400"
                    title="Delete"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Preview Tab
// ---------------------------------------------------------------------------

function PreviewTab({
  project,
}: Readonly<{
  project: PresentationProjectResponse;
}>) {
  const [previews, setPreviews] = useState<{ slideNumber: number; thumbnailUrl: string; title: string }[]>([]);
  const [loadingPreviews, setLoadingPreviews] = useState(false);
  const [selectedSlide, setSelectedSlide] = useState<number | null>(null);
  const [fullscreen, setFullscreen] = useState(false);

  useEffect(() => {
    if (project.status === "generated" || project.status === "complete" || project.status === "refining") {
      loadPreviews();
    }
  }, [project.uuid, project.status, project.versions?.length]);

  const loadPreviews = async () => {
    setLoadingPreviews(true);
    try {
      const response = await presentationsService.getPreview(project.uuid);
      setPreviews(response.slides);
      if (response.slides.length > 0) {
        setSelectedSlide(response.slides[0].slideNumber);
      }
    } catch (err) {
      console.error("Failed to load previews:", err);
    } finally {
      setLoadingPreviews(false);
    }
  };

  const selectedPreview = previews.find((p) => p.slideNumber === selectedSlide);

  if (project.status === "generating") {
    return (
      <div className="flex flex-col items-center justify-center h-full">
        <Loader2 className="w-8 h-8 animate-spin text-mutual-orange mb-3" />
        <p className="text-sm font-medium text-content-primary">Generating slides...</p>
        <p className="text-xs text-content-tertiary mt-1">This may take 30-60 seconds</p>
      </div>
    );
  }

  if (loadingPreviews) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-6 h-6 animate-spin text-content-tertiary" />
      </div>
    );
  }

  if (previews.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-center">
        <Layers className="w-10 h-10 text-content-tertiary mb-2" />
        <p className="text-sm text-content-tertiary">
          No slide previews available yet. Generate slides from the Outline tab.
        </p>
      </div>
    );
  }

  // Fullscreen lightbox
  if (fullscreen && selectedPreview) {
    return (
      <div
        className="fixed inset-0 z-50 bg-black flex items-center justify-center"
        role="presentation" // NOSONAR — decorative backdrop element
        onClick={() => setFullscreen(false)}
        onKeyDown={(e) => {
          if (e.key === "Escape") setFullscreen(false);
        }}
      >
        <button
          onClick={() => setFullscreen(false)}
          className="absolute top-4 right-4 p-2 rounded-lg bg-white/10 text-white hover:bg-white/20 z-10"
        >
          <Minimize2 className="w-5 h-5" />
        </button>
        <button
          onClick={(e) => {
            e.stopPropagation();
            const idx = previews.findIndex((p) => p.slideNumber === selectedSlide);
            if (idx > 0) setSelectedSlide(previews[idx - 1].slideNumber);
          }}
          className="absolute left-4 p-2 rounded-lg bg-white/10 text-white hover:bg-white/20 z-10"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        <button
          onClick={(e) => {
            e.stopPropagation();
            const idx = previews.findIndex((p) => p.slideNumber === selectedSlide);
            if (idx < previews.length - 1) setSelectedSlide(previews[idx + 1].slideNumber);
          }}
          className="absolute right-4 p-2 rounded-lg bg-white/10 text-white hover:bg-white/20 z-10"
        >
          <ChevronRight className="w-5 h-5" />
        </button>
        <AuthImage
          src={selectedPreview.thumbnailUrl}
          alt={selectedPreview.title}
          className="max-w-[90vw] max-h-[90vh] object-contain"
          onClick={(e) => e.stopPropagation()}
        />
        <div className="absolute bottom-4 text-white text-sm bg-black/50 px-3 py-1.5 rounded-lg">
          Slide {selectedPreview.slideNumber} of {previews.length} - {selectedPreview.title}
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full">
      {/* Slide Filmstrip */}
      <div className="w-48 shrink-0 border-r border-border-primary overflow-auto p-3 space-y-2">
        {previews.map((preview) => (
          <button
            key={preview.slideNumber}
            onClick={() => setSelectedSlide(preview.slideNumber)}
            className={`w-full rounded-lg border overflow-hidden transition-all ${
              selectedSlide === preview.slideNumber
                ? "border-mutual-orange ring-1 ring-mutual-orange/30"
                : "border-border-primary hover:border-border-secondary"
            }`}
          >
            {preview.thumbnailUrl ? (
              <AuthImage src={preview.thumbnailUrl} alt={preview.title} className="w-full aspect-video object-cover" />
            ) : (
              <div className="w-full aspect-video bg-surface-secondary flex items-center justify-center">
                <Presentation className="w-6 h-6 text-content-tertiary" />
              </div>
            )}
            <div className="px-2 py-1 bg-surface-primary">
              <span className="text-[10px] text-content-tertiary">
                {preview.slideNumber}. {preview.title}
              </span>
            </div>
          </button>
        ))}
      </div>

      {/* Main Preview */}
      <div className="flex-1 flex flex-col items-center justify-center p-6 bg-surface-secondary/30">
        {selectedPreview ? (
          <>
            <div className="relative w-full max-w-4xl">
              {selectedPreview.thumbnailUrl ? (
                <AuthImage
                  src={selectedPreview.thumbnailUrl}
                  alt={selectedPreview.title}
                  className="w-full rounded-xl shadow-lg border border-border-primary"
                />
              ) : (
                <div className="w-full aspect-video rounded-xl bg-surface-primary border border-border-primary flex items-center justify-center">
                  <div className="text-center">
                    <Presentation className="w-12 h-12 text-content-tertiary mx-auto mb-2" />
                    <p className="text-sm text-content-tertiary">No preview available</p>
                  </div>
                </div>
              )}
              <button
                onClick={() => setFullscreen(true)}
                className="absolute top-3 right-3 p-2 rounded-lg bg-black/40 text-white hover:bg-black/60 transition-colors"
                title="Fullscreen"
              >
                <Maximize2 className="w-4 h-4" />
              </button>
            </div>
            <div className="mt-3 text-center">
              <h3 className="text-sm font-medium text-content-primary">
                Slide {selectedPreview.slideNumber} - {selectedPreview.title}
              </h3>
              <p className="text-xs text-content-tertiary mt-0.5">
                {previews.findIndex((p) => p.slideNumber === selectedSlide) + 1} of {previews.length} slides
              </p>
            </div>
          </>
        ) : (
          <p className="text-sm text-content-tertiary">Select a slide to preview</p>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Refine Tab
// ---------------------------------------------------------------------------

function RefineTab({
  project,
  onProjectUpdate,
  history,
  onHistoryChange,
}: Readonly<{
  project: PresentationProjectResponse;
  onProjectUpdate: (p: PresentationProjectResponse) => void;
  history: ChatMessage[];
  onHistoryChange: (h: ChatMessage[]) => void;
}>) {
  const { trackOperation } = useOperations();
  const [instructions, setInstructions] = useState("");
  const [slideNumbers, setSlideNumbers] = useState("");
  const [refining, setRefining] = useState(false);
  const [statusMessage, setStatusMessage] = useState("");
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Keep a ref so the async stream handler always reads the latest history
  const historyRef = useRef(history);
  historyRef.current = history;

  const appendHistory = (msgs: ChatMessage[]) => {
    onHistoryChange([...historyRef.current, ...msgs]);
  };

  // Operation stream for refinement
  const [refineOperationId, setRefineOperationId] = useState<string | null>(null);

  const handleRefineEvent = useCallback(
    (event: OperationEvent) => {
      const { event: eventName, data } = event;

      switch (eventName) {
        case "status": {
          const d = data as { message?: string };
          setStatusMessage(d.message || "");
          break;
        }
        case "refine_end": {
          const d = data as { versionNumber?: number; slideCount?: number; generationTimeSeconds?: number };
          appendHistory([
            {
              role: "assistant",
              content: `Refinement complete! Created version ${d.versionNumber} with ${d.slideCount} slides (${d.generationTimeSeconds}s).`,
            },
          ]);
          // Reload project
          presentationsService.getProject(project.uuid).then((response) => {
            onProjectUpdate(response.data);
          });
          break;
        }
        case "operation_completed": {
          setRefining(false);
          setStatusMessage("");
          setRefineOperationId(null);
          break;
        }
        case "operation_failed": {
          const d = data as { error?: string };
          appendHistory([{ role: "assistant", content: `Error: ${d.error || "Refinement failed"}` }]);
          setRefining(false);
          setStatusMessage("");
          setRefineOperationId(null);
          break;
        }
        case "error": {
          const d = data as { message?: string };
          appendHistory([{ role: "assistant", content: `Error: ${d.message}` }]);
          break;
        }
      }
    },
    [project.uuid, onProjectUpdate],
  );

  useOperationStream(refineOperationId, {
    onEvent: handleRefineEvent,
    onError: (err) => {
      console.error("Refine stream error:", err);
      appendHistory([{ role: "assistant", content: `Refinement failed: ${err}` }]);
      setRefining(false);
      setStatusMessage("");
      setRefineOperationId(null);
    },
  });

  const handleRefine = async () => {
    if (!instructions.trim()) return;

    const parsedSlides = slideNumbers
      .split(",")
      .map((s) => Number.parseInt(s.trim(), 10))
      .filter((n) => !Number.isNaN(n));

    const parsedSlideSuffix = parsedSlides.length > 0 ? ` (slides: ${parsedSlides.join(", ")})` : "";

    setRefining(true);
    setStatusMessage("Starting refinement...");
    appendHistory([
      {
        role: "user",
        content: `${instructions}${parsedSlideSuffix}`,
      },
    ]);
    setInstructions("");
    setSlideNumbers("");

    try {
      const result = await presentationsService.refineSlides(project.uuid, {
        instructions: instructions.trim(),
        slideNumbers: parsedSlides.length > 0 ? parsedSlides : undefined,
      });
      setRefineOperationId(result.operation_id);
      trackOperation({
        operation_id: result.operation_id,
        operation_type: "presentation_refine",
        entity_id: project.uuid,
        status: "running",
        title: project.name || "Slide refinement",
        started_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        user_id: 0,
      });
    } catch (err) {
      console.error("Refinement failed:", err);
      appendHistory([
        {
          role: "assistant",
          content: `Refinement failed: ${err instanceof Error ? err.message : "Unknown error"}`,
        },
      ]);
      setRefining(false);
      setStatusMessage("");
    }
  };

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [history]);

  return (
    <div className="flex flex-col h-full">
      {/* Chat History */}
      <div className="flex-1 overflow-auto p-6 space-y-4">
        {history.length === 0 && (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <RefreshCw className="w-10 h-10 text-content-tertiary mb-2" />
            <h3 className="text-sm font-medium text-content-primary mb-1">Refine Your Slides</h3>
            <p className="text-xs text-content-tertiary max-w-sm">
              Describe what changes you'd like to make. You can target specific slides by number, or make changes to the
              entire presentation.
            </p>
          </div>
        )}
        {history.map((msg, i) => (
          <div
            key={`refine-${msg.role}-${i}`}
            className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
          >
            <div
              className={`max-w-lg px-4 py-2.5 rounded-xl text-sm ${
                msg.role === "user"
                  ? "bg-mutual-orange/10 text-content-primary"
                  : "bg-surface-secondary text-content-primary border border-border-primary"
              }`}
            >
              {msg.content}
            </div>
          </div>
        ))}
        {refining && statusMessage && (
          <div className="flex justify-start">
            <div className="max-w-lg px-4 py-2.5 rounded-xl text-sm bg-surface-secondary text-content-tertiary border border-border-primary flex items-center gap-2">
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
              {statusMessage}
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input Area */}
      <div className="shrink-0 border-t border-border-primary p-4">
        <div className="flex items-end gap-3 max-w-3xl mx-auto">
          <div className="flex-1 space-y-2">
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={slideNumbers}
                onChange={(e) => setSlideNumbers(e.target.value)}
                placeholder="Slide #s (e.g. 1,3,5) — leave blank for all"
                className="w-48 px-3 py-1.5 rounded-lg bg-surface-secondary border border-border-primary text-content-primary text-xs placeholder:text-content-tertiary focus:outline-hidden focus:ring-1 focus:ring-mutual-orange/50"
              />
            </div>
            <textarea
              value={instructions}
              onChange={(e) => setInstructions(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleRefine();
                }
              }}
              placeholder="Describe the changes you'd like... (e.g. 'Make the title slide more impactful', 'Add data charts to slide 3')"
              rows={2}
              className="w-full px-3 py-2 rounded-lg bg-surface-secondary border border-border-primary text-content-primary text-sm placeholder:text-content-tertiary focus:outline-hidden focus:ring-1 focus:ring-mutual-orange/50 resize-none"
              disabled={refining}
            />
          </div>
          <button
            onClick={handleRefine}
            disabled={!instructions.trim() || refining}
            className="shrink-0 p-2.5 rounded-lg bg-brand-gradient text-white hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {refining ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </button>
        </div>
      </div>
    </div>
  );
}
